import React from 'react';
import { Bot, Timer, Hash, Sparkles, Globe, Link, Info, Image, Brain, Lightbulb, MessageSquare, Fingerprint } from 'lucide-react';

const renderMetadataIcon = (key: string) => {
  switch (key) {
    case 'model':
      return <Bot className="w-4 h-4" />;
    case 'processing_time':
      return <Timer className="w-4 h-4" />;
    case 'tokens_used':
      return <Hash className="w-4 h-4" />;
    case 'prompt':
      return <Sparkles className="w-4 h-4" />;
    case 'webhook_url':
      return <Link className="w-4 h-4" />;
    case 'webhook_info':
      return <Info className="w-4 h-4" />;
    case 'photo_prompt':
      return <Image className="w-4 h-4" />;
    case 'info':
      return <Brain className="w-4 h-4" />;
    case 'strategy':
      return <Lightbulb className="w-4 h-4" />;
    case 'engagement_tips':
      return <MessageSquare className="w-4 h-4" />;
    case 'execution_id':
      return <Fingerprint className="w-4 h-4" />;
    default:
      return <Globe className="w-4 h-4" />;
  }
};

const formatMetadataValue = (key: string, value: any) => {
  switch (key) {
    case 'processing_time':
      return `${value} seconds`;
    case 'tokens_used':
      return `${value} tokens`;
    case 'webhook_url':
      return value === import.meta.env.VITE_DEFAULT_WEBHOOK 
        ? 'AI Agent HUB AI Creator'
        : (
          <a 
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 break-all"
            title={value}
          >
            {value}
          </a>
        );
    case 'webhook_info':
      return (
        <div className="bg-blue-50 rounded p-2 mt-1">
          <p className="text-blue-700">{value}</p>
        </div>
      );
    case 'photo_prompt':
    case 'info':
    case 'strategy':
    case 'engagement_tips':
      return (
        <div className="bg-gray-50 rounded p-2 mt-1">
          <p className="text-gray-700 whitespace-pre-wrap">{value}</p>
        </div>
      );
    case 'webhook_response':
      if (typeof value === 'object') {
        return 'Response received';
      }
      return String(value);
    case 'error_details':
      if (typeof value === 'object') {
        return (
          <div className="bg-red-50 rounded p-2 mt-1">
            <p className="text-red-700 whitespace-pre-wrap">{JSON.stringify(value, null, 2)}</p>
          </div>
        );
      }
      return String(value);
    case 'interruption_history':
      if (Array.isArray(value)) {
        return (
          <div className="bg-yellow-50 rounded p-2 mt-1">
            {value.map((entry, index) => (
              <div key={index} className="text-yellow-700 mb-1">
                {entry.timestamp}: {entry.reason}
              </div>
            ))}
          </div>
        );
      }
      return String(value);
    default:
      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value, null, 2);
      }
      return String(value);
  }
};

const formatMetadataKey = (key: string) => {
  const translations: { [key: string]: string } = {
    'model': 'AI Model',
    'processing_time': 'Processing Time',
    'tokens_used': 'Tokens Used',
    'prompt': 'Prompt',
    'webhook_url': 'Webhook',
    'webhook_info': 'Webhook Info',
    'scheduled_for': 'Scheduled For',
    'test_timestamp': 'Test Time',
    'preview_requested_at': 'Preview Requested',
    'is_auto_test': 'Auto Test',
    'stage': 'Stage',
    'is_test': 'Test Mode',
    'platform': 'Platform',
    'modify_image': 'Modify Image',
    'generate_image': 'Generate Image',
    'is_in_progress': 'In Progress',
    'creation_timestamp': 'Created At',
    'completion_timestamp': 'Completed At',
    'publish_start_timestamp': 'Publishing Started',
    'webhook_response': 'Webhook Response',
    'error_details': 'Error Details',
    'interruption_history': 'Interruption History',
    'last_error': 'Last Error',
    'error_count': 'Error Count',
    'retry_count': 'Retry Count',
    'last_retry': 'Last Retry',
    'photo_prompt': 'Image Generation Prompt',
    'info': 'Post Analysis',
    'strategy': 'Content Strategy',
    'engagement_tips': 'Engagement Tips',
    'webhook_response_data': 'Response Data',
    'webhook_response_metadata': 'Response Metadata',
    'execution_id': 'Execution ID'
  };
  return translations[key] || key;
};

const shouldShowMetadata = (key: string): boolean => {
  const hiddenKeys = [
    'webhook_request_id',
    'webhook_request_timestamp',
    'webhook_response_timestamp'
  ];
  return !hiddenKeys.includes(key);
};

const extractWebhookInfo = (metadata: any): string | null => {
  if (!metadata) return null;

  // Handle array response format
  let webhookResponse = metadata.webhook_response;
  if (Array.isArray(webhookResponse)) {
    webhookResponse = webhookResponse[0];
  }
  
  // Get data from response
  const responseData = webhookResponse?.data;
  if (!responseData) return null;

  const info = [];
  
  if (responseData.content) {
    info.push('Content generated by AI');
  }
  
  if (responseData.image_url) {
    info.push('Image processed by AI');
  }

  if (responseData.metadata?.model) {
    info.push(`Using ${responseData.metadata.model} model`);
  }

  if (responseData.metadata?.processing_time) {
    info.push(`Processed in ${responseData.metadata.processing_time} seconds`);
  }

  return info.length > 0 ? info.join(' • ') : null;
};

// Helper function to flatten webhook response metadata
const flattenWebhookMetadata = (metadata: any) => {
  if (!metadata) return {};
  
  // Handle array response format
  let webhookResponse = metadata.webhook_response;
  if (Array.isArray(webhookResponse)) {
    webhookResponse = webhookResponse[0];
  }
  
  if (!webhookResponse?.data?.metadata) {
    return metadata;
  }

  const webhookMetadata = webhookResponse.data.metadata;
  return {
    ...metadata,
    ...webhookMetadata,
    photo_prompt: webhookMetadata.photo_prompt,
    info: webhookMetadata.info,
    strategy: webhookMetadata.strategy,
    engagement_tips: webhookMetadata.engagement_tips,
    original_webhook_response: metadata.webhook_response
  };
};

interface PostMetadataProps {
  metadata: Record<string, any>;
}

export const PostMetadata: React.FC<PostMetadataProps> = ({ metadata }) => {
  if (!metadata) return null;

  // Extract webhook info
  const webhookInfo = extractWebhookInfo(metadata);
  
  // Flatten webhook metadata
  const displayMetadata = flattenWebhookMetadata({
    ...metadata,
    ...(webhookInfo && { webhook_info: webhookInfo })
  });

  // Sort metadata keys for better display
  const sortOrder: { [key: string]: number } = {
    'execution_id': 0,
    'stage': 1,
    'platform': 2,
    'is_test': 3,
    'info': 4,
    'strategy': 5,
    'engagement_tips': 6,
    'photo_prompt': 7,
    'model': 8,
    'processing_time': 9,
    'tokens_used': 10,
    'webhook_info': 11,
    'webhook_url': 12,
    'error_details': 13,
    'interruption_history': 14,
    'creation_timestamp': 15,
    'completion_timestamp': 16,
    'publish_start_timestamp': 17,
    'last_error': 18,
    'error_count': 19,
    'retry_count': 20,
    'last_retry': 21
  };

  const sortedMetadata = Object.entries(displayMetadata)
    .filter(([key]) => shouldShowMetadata(key))
    .sort(([keyA], [keyB]) => {
      const orderA = sortOrder[keyA] || 100;
      const orderB = sortOrder[keyB] || 100;
      return orderA - orderB;
    });

  return (
    <div className="mt-4 pt-4 border-t">
      <h4 className="text-sm font-medium text-gray-700 mb-2">Metadata</h4>
      <div className="space-y-2">
        {sortedMetadata.map(([key, value]) => (
          <div key={key} className="flex items-start gap-2 text-sm">
            <div className="text-gray-400 mt-0.5">
              {renderMetadataIcon(key)}
            </div>
            <div className="min-w-0 flex-1">
              <span className="font-medium text-gray-700 block">
                {formatMetadataKey(key)}:
              </span>
              <div className="text-gray-600 break-words">
                {formatMetadataValue(key, value)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};